import { useState, useEffect, useMemo } from "react";
import { X, LoaderCircle, Search } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Card } from "./ui/card";

const API = import.meta.env.VITE_API_URL || "http://localhost:9000";

type UserRow = {
  id: string;
  name: string;
  email: string | null;
  phoneNumber: string | null;
  organizations: { role: string; orgName: string; orgId: string }[];
};

interface Props {
  isOpen: boolean;
  organizationId: string;
  organizationName: string;
  onClose: () => void;
  onSuccess: () => void;
}

export function AddExistingUserModal({ isOpen, organizationId, organizationName, onClose, onSuccess }: Props) {
  const [users, setUsers] = useState<UserRow[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [role, setRole] = useState("member");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    setLoadingList(true);
    setError("");
    fetch(`${API}/api/admin/users`, { credentials: "include" })
      .then((r) => r.json())
      .then((data) => setUsers(Array.isArray(data) ? data : []))
      .catch(() => setError("Kunne ikke hente brukere"))
      .finally(() => setLoadingList(false));
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setQuery("");
      setSelectedUserId(null);
      setRole("member");
      setError("");
    }
  }, [isOpen]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users.slice(0, 20);
    return users
      .filter((u) => {
        const alreadyMember = u.organizations.some((o) => o.orgId === organizationId);
        if (alreadyMember) return false;
        return (
          (u.name && u.name.toLowerCase().includes(q)) ||
          (u.email && u.email.toLowerCase().includes(q)) ||
          (u.phoneNumber && u.phoneNumber.toLowerCase().includes(q))
        );
      })
      .slice(0, 20);
  }, [users, query, organizationId]);

  if (!isOpen) return null;

  async function handleSubmit() {
    if (!selectedUserId) return;
    setSubmitting(true);
    setError("");
    try {
      const response = await fetch(`${API}/api/admin/organizations/${organizationId}/members`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ userId: selectedUserId, role }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error((data as any).error || "Kunne ikke legge til bruker");
      }
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  const selectedUser = users.find((u) => u.id === selectedUserId);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <Card className="w-full max-w-lg shadow-2xl animate-in fade-in zoom-in duration-200 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-bold">Legg til eksisterende bruker</h2>
            <p className="text-sm text-muted-foreground mt-1">til {organizationName}</p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8">
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          {error && (
            <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-lg">{error}</div>
          )}

          <div className="space-y-2">
            <Label htmlFor="search">Søk etter bruker</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="search"
                placeholder="Navn, e-post eller telefon"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="pl-9"
                autoFocus
              />
            </div>
          </div>

          <div className="border border-border rounded-lg divide-y divide-border max-h-64 overflow-y-auto">
            {loadingList ? (
              <div className="flex items-center justify-center p-6">
                <LoaderCircle className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground">
                {query ? "Ingen treff" : "Skriv for å søke"}
              </div>
            ) : (
              filtered.map((u) => {
                const isSelected = u.id === selectedUserId;
                return (
                  <button
                    type="button"
                    key={u.id}
                    onClick={() => setSelectedUserId(u.id)}
                    className={`w-full text-left p-3 transition-colors ${
                      isSelected ? "bg-primary/10" : "hover:bg-muted/50"
                    }`}
                  >
                    <div className="font-medium text-sm">{u.name || "Uten navn"}</div>
                    <div className="text-xs text-muted-foreground">
                      {[u.email, u.phoneNumber].filter(Boolean).join(" • ") || "—"}
                    </div>
                    {u.organizations.length > 0 && (
                      <div className="text-xs text-muted-foreground mt-1">
                        Medlem i: {u.organizations.map((o) => o.orgName).join(", ")}
                      </div>
                    )}
                  </button>
                );
              })
            )}
          </div>

          {selectedUser && (
            <div className="space-y-2 pt-2 border-t border-border">
              <Label htmlFor="role">Rolle i organisasjon</Label>
              <select
                id="role"
                className="w-full h-10 px-3 py-2 text-sm rounded-md border border-input bg-background focus:outline-none focus:ring-2 focus:ring-primary/20 appearance-none"
                value={role}
                onChange={(e) => setRole(e.target.value)}
              >
                <option value="member">Bruker</option>
                <option value="admin">Admin</option>
                <option value="owner">Eier</option>
              </select>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 p-6 border-t border-border">
          <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
            Avbryt
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={!selectedUserId || submitting}>
            {submitting && <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />}
            Legg til
          </Button>
        </div>
      </Card>
    </div>
  );
}
